// Iter 234 — Admin User Management page.
//
// Surfaces /api/admin/users so an admin can view, search, disable,
// reset password, promote/demote, and hard-delete any registered
// user.  Mirrors the styling of AdminHealthPage so admins move
// between admin tools without re-learning the chrome.

import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { useAuth } from "../context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Users, ShieldCheck, ShieldX, Search, KeyRound, Trash2, Ban,
  CheckCircle2, RefreshCcw, Copy, Crown, Mail,
} from "lucide-react";
import TopNav from "../components/TopNav";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return "—"; }
};

const StatTile = ({ label, value, Icon, tone, testid }) => (
  <div className="nd-card p-4" data-testid={testid}>
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="eyebrow">{label}</p>
        <p className={`mt-2 font-display font-bold text-2xl font-tabular ${tone || "text-foreground"}`}>
          {value}
        </p>
      </div>
      <div className="h-9 w-9 rounded-xl bg-muted flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-foreground" />
      </div>
    </div>
  </div>
);

const RowActions = ({ user, onPatch, onResetPassword, onDelete, currentAdminId }) => {
  const isSelf = user.id === currentAdminId;
  return (
    <div className="flex items-center gap-1 flex-wrap justify-end">
      {/* Toggle admin */}
      <Button
        size="sm"
        variant="ghost"
        disabled={isSelf}
        title={isSelf ? "You can't change your own admin flag" : user.is_admin ? "Remove admin" : "Promote to admin"}
        onClick={() => onPatch(user, { is_admin: !user.is_admin })}
        data-testid={`adminusers-row-${user.id}-toggle-admin`}
      >
        {user.is_admin ? <ShieldX className="w-4 h-4 text-amber-300" /> : <ShieldCheck className="w-4 h-4 text-emerald-300" />}
      </Button>
      {/* Toggle disabled */}
      <Button
        size="sm"
        variant="ghost"
        disabled={isSelf}
        title={isSelf ? "You can't disable yourself" : user.disabled ? "Re-enable account" : "Disable account"}
        onClick={() => onPatch(user, { disabled: !user.disabled })}
        data-testid={`adminusers-row-${user.id}-toggle-disabled`}
      >
        {user.disabled ? <CheckCircle2 className="w-4 h-4 text-emerald-300" /> : <Ban className="w-4 h-4 text-rose-300" />}
      </Button>
      {/* Reset password */}
      <Button
        size="sm"
        variant="ghost"
        onClick={() => onResetPassword(user)}
        title="Generate a new password"
        data-testid={`adminusers-row-${user.id}-reset-pwd`}
      >
        <KeyRound className="w-4 h-4 text-cyan-300" />
      </Button>
      {/* Delete */}
      <Button
        size="sm"
        variant="ghost"
        disabled={isSelf}
        onClick={() => onDelete(user)}
        title={isSelf ? "You can't delete yourself" : "Hard-delete user + all owned data"}
        data-testid={`adminusers-row-${user.id}-delete`}
      >
        <Trash2 className="w-4 h-4 text-rose-300" />
      </Button>
    </div>
  );
};

export default function AdminUsersPage() {
  const { user: currentUser, token } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [limit] = useState(50);
  const [skip, setSkip] = useState(0);
  const [search, setSearch] = useState("");
  const [filterAdmin, setFilterAdmin] = useState("all"); // all | yes | no
  const [filterDisabled, setFilterDisabled] = useState("all");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const [resetResult, setResetResult] = useState(null); // {email, new_password}
  const [deleteTarget, setDeleteTarget] = useState(null); // user pending delete confirm

  const authHeaders = useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token],
  );

  // Bounce non-admins back to the home page.
  useEffect(() => {
    if (currentUser && !currentUser.is_admin) navigate("/", { replace: true });
  }, [currentUser, navigate]);

  const loadStats = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/users/stats`, { headers: authHeaders });
      setStats(r.data);
    } catch (e) {
      // Silently — stats are nice-to-have, the list is the critical view
    }
  }, [authHeaders]);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const params = { limit, skip };
      if (search.trim()) params.q = search.trim();
      if (filterAdmin === "yes") params.is_admin = true;
      if (filterAdmin === "no")  params.is_admin = false;
      if (filterDisabled === "yes") params.disabled = true;
      if (filterDisabled === "no")  params.disabled = false;
      const r = await axios.get(`${API}/admin/users`, { headers: authHeaders, params });
      setUsers(r.data.users);
      setTotal(r.data.total);
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      setErr(typeof detail === "string" ? detail : "Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [authHeaders, limit, skip, search, filterAdmin, filterDisabled]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handlePatch = async (user, patch) => {
    try {
      const r = await axios.patch(`${API}/admin/users/${user.id}`, patch, { headers: authHeaders });
      // Replace in the local list
      setUsers((prev) => prev.map((u) => (u.id === user.id ? r.data : u)));
      const label = Object.keys(patch).join(", ");
      toast.success(`${user.email}: ${label} updated`);
      loadStats();
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast.error(typeof detail === "string" ? detail : "Update failed");
    }
  };

  const handleResetPassword = async (user) => {
    if (!window.confirm(`Generate a new random password for ${user.email}?`)) return;
    try {
      const r = await axios.post(`${API}/admin/users/${user.id}/reset-password`, {}, { headers: authHeaders });
      setResetResult(r.data);
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast.error(typeof detail === "string" ? detail : "Reset failed");
    }
  };

  const handleDelete = async () => {
    const u = deleteTarget;
    if (!u) return;
    try {
      const r = await axios.delete(`${API}/admin/users/${u.id}`, { headers: authHeaders });
      setUsers((prev) => prev.filter((x) => x.id !== u.id));
      setTotal((t) => Math.max(0, t - 1));
      const cascadeStr = Object.entries(r.data.cascade || {})
        .filter(([, n]) => n > 0)
        .map(([col, n]) => `${col}=${n}`)
        .join(", ");
      toast.success(`${u.email} deleted${cascadeStr ? ` (${cascadeStr})` : ""}`);
      loadStats();
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast.error(typeof detail === "string" ? detail : "Delete failed");
    } finally {
      setDeleteTarget(null);
    }
  };

  const copy = (text) => {
    try { navigator.clipboard.writeText(text); toast.success("Copied"); }
    catch { /* ignore */ }
  };

  // ── Render ────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-background">
      <TopNav />
      <main className="container py-6 sm:py-8" data-testid="admin-users-page">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-display font-bold">User Management</h1>
            <p className="text-sm text-muted-foreground mt-1">
              View, search, disable, promote, reset, or delete any registered account.
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { loadStats(); loadUsers(); }}
            data-testid="admin-users-refresh"
          >
            <RefreshCcw className="w-4 h-4 mr-1.5" />
            Refresh
          </Button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <StatTile label="Total users" value={stats.total} Icon={Users} testid="admin-users-stat-total" />
            <StatTile label="Admins" value={stats.admins} Icon={Crown} tone="text-emerald-300" testid="admin-users-stat-admins" />
            <StatTile label="Disabled" value={stats.disabled} Icon={Ban} tone={stats.disabled > 0 ? "text-rose-300" : "text-foreground"} testid="admin-users-stat-disabled" />
            <StatTile label="Betway-connected" value={stats.betway_connected} Icon={ShieldCheck} testid="admin-users-stat-betway" />
          </div>
        )}

        {/* Filter bar */}
        <div className="nd-card p-3 mb-4 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by email…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setSkip(0); }}
              className="pl-8 h-9"
              data-testid="admin-users-search"
            />
          </div>
          <select
            value={filterAdmin}
            onChange={(e) => { setFilterAdmin(e.target.value); setSkip(0); }}
            className="bg-muted text-sm rounded-md px-2 py-1.5 border border-white/10"
            data-testid="admin-users-filter-admin"
          >
            <option value="all">All roles</option>
            <option value="yes">Admins only</option>
            <option value="no">Regular only</option>
          </select>
          <select
            value={filterDisabled}
            onChange={(e) => { setFilterDisabled(e.target.value); setSkip(0); }}
            className="bg-muted text-sm rounded-md px-2 py-1.5 border border-white/10"
            data-testid="admin-users-filter-disabled"
          >
            <option value="all">All status</option>
            <option value="no">Active only</option>
            <option value="yes">Disabled only</option>
          </select>
          <span className="text-xs text-muted-foreground font-tabular ml-auto" data-testid="admin-users-count">
            {loading ? "Loading…" : `${total} match${total === 1 ? "" : "es"}`}
          </span>
        </div>

        {/* Error */}
        {err && (
          <div className="nd-card p-3 mb-3 border-rose-500/40 bg-rose-500/[0.04] text-sm text-rose-200" data-testid="admin-users-error">
            {err}
          </div>
        )}

        {/* Users table — mobile-friendly card list */}
        <div className="space-y-2" data-testid="admin-users-list">
          {users.map((u) => {
            const isSelf = u.id === currentUser?.id;
            return (
              <div
                key={u.id}
                className={[
                  "nd-card p-3 flex items-start gap-3 flex-wrap",
                  u.disabled ? "border-rose-500/30 bg-rose-500/[0.03]" : "",
                ].join(" ")}
                data-testid={`adminusers-row-${u.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Mail className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                    <span className="font-mono text-sm font-semibold truncate" data-testid={`adminusers-row-${u.id}-email`}>
                      {u.email}
                    </span>
                    {isSelf && (
                      <Badge variant="outline" className="text-[9px] py-0 px-1.5 border-cyan-500/40 text-cyan-200">
                        YOU
                      </Badge>
                    )}
                    {u.is_admin && (
                      <Badge className="text-[9px] py-0 px-1.5 bg-emerald-500/20 text-emerald-200 border-emerald-500/40">
                        ADMIN
                      </Badge>
                    )}
                    {u.disabled && (
                      <Badge className="text-[9px] py-0 px-1.5 bg-rose-500/20 text-rose-200 border-rose-500/40">
                        DISABLED
                      </Badge>
                    )}
                    {u.has_betway_account && (
                      <Badge variant="outline" className="text-[9px] py-0 px-1.5 border-amber-500/40 text-amber-200">
                        BETWAY
                      </Badge>
                    )}
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                    <span>Joined {fmtDate(u.created_at)}</span>
                    {u.last_login_at && <span>· Last login {fmtDate(u.last_login_at)}</span>}
                    <span>· {u.language?.toUpperCase()} / {u.currency}</span>
                    {u.tv_country && <span>· TV {u.tv_country}</span>}
                    {u.failed_login_count > 0 && (
                      <span className="text-rose-300">· {u.failed_login_count} failed login{u.failed_login_count === 1 ? "" : "s"}</span>
                    )}
                  </div>
                </div>
                <RowActions
                  user={u}
                  currentAdminId={currentUser?.id}
                  onPatch={handlePatch}
                  onResetPassword={handleResetPassword}
                  onDelete={(usr) => setDeleteTarget(usr)}
                />
              </div>
            );
          })}
          {!loading && !users.length && (
            <div className="nd-card p-6 text-center text-sm text-muted-foreground" data-testid="admin-users-empty">
              No users match these filters.
            </div>
          )}
        </div>

        {/* Pagination */}
        {total > limit && (
          <div className="flex items-center justify-center gap-2 mt-4" data-testid="admin-users-pagination">
            <Button
              size="sm" variant="outline"
              disabled={skip === 0}
              onClick={() => setSkip((s) => Math.max(0, s - limit))}
              data-testid="admin-users-prev"
            >
              ← Prev
            </Button>
            <span className="text-xs text-muted-foreground font-tabular">
              {skip + 1}–{Math.min(skip + limit, total)} of {total}
            </span>
            <Button
              size="sm" variant="outline"
              disabled={skip + limit >= total}
              onClick={() => setSkip((s) => s + limit)}
              data-testid="admin-users-next"
            >
              Next →
            </Button>
          </div>
        )}
      </main>

      {/* Reset-password result dialog (shown once — admin must copy the password now) */}
      <AlertDialog open={!!resetResult} onOpenChange={(o) => !o && setResetResult(null)}>
        <AlertDialogContent data-testid="admin-users-reset-result">
          <AlertDialogHeader>
            <AlertDialogTitle>Password reset for {resetResult?.email}</AlertDialogTitle>
            <AlertDialogDescription>
              This password is shown <strong>only once</strong>.  Copy it now and send it to the user via a secure channel.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="bg-muted rounded-md p-3 flex items-center gap-2 font-mono text-sm select-all" data-testid="admin-users-reset-password">
            <span className="flex-1 break-all">{resetResult?.new_password}</span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => copy(resetResult?.new_password || "")}
              data-testid="admin-users-reset-copy"
            >
              <Copy className="w-3.5 h-3.5 mr-1" /> Copy
            </Button>
          </div>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setResetResult(null)}>Done</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="admin-users-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteTarget?.email}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will hard-delete the user account <strong>and cascade-delete every owned record</strong>
              (Betway bets, slips, snapshot history, strategy configs).  This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-users-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-rose-500 hover:bg-rose-600"
              onClick={handleDelete}
              data-testid="admin-users-delete-confirm"
            >
              Delete forever
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
